import '../../../components/text-and-lang/as-text.ts';
import '../../../components/parameters/as-param-group.ts';
import '../../../components/inputs/buttons/as-button.ts';
import { applyLocalizedText, applyLabelVars } from '../../../components/base.ts';
import { sendEv } from '../../../data/events.ts';

/**
 * <as-export-preview-panel>
 *
 * Preview card placed as a sibling before <as-export-group> inside an
 * <as-step-panel>. Uses display:contents (light DOM) so the inner as-param-group
 * card participates directly in the step panel's flex layout.
 *
 * Children placed inside this element in HTML or JS are moved into the card at
 * build time and automatically disabled while preview is inactive, re-enabled when
 * the "Show export preview" checkbox is checked. Children added later via JS are
 * also handled via MutationObserver.
 *
 * Attributes
 *   subheading-default-text   — card heading (default: "Design preview")
 *   subheading-dict-key       — i18n key for the heading
 *   descr-default-text        — override for the description paragraph text
 *   descr-text-dict-key       — i18n key for the description paragraph
 *
 * Events
 *   as-preview-toggle   bubbles, composed — detail: { active: boolean }
 *
 * API
 *   get previewActive: boolean
 */
export class AsExportPreviewPanel extends HTMLElement {
  static readonly tag = 'as-export-preview-panel' as const;

  private _previewActive = false;
  private _built = false;
  private _toggleBtnEl: HTMLElement | null = null;
  private _cardEl: HTMLElement | null = null;
  private _descTextEl: HTMLElement | null = null;
  private _userEls: HTMLElement[] = [];
  private _observer: MutationObserver | null = null;
  private _onResetBound = (): void => this._handleSessionReset();
  // Placeholder values for {0}, {1}, ... in the subheading/description — set
  // programmatically, no attribute equivalent (these are markup, vars are runtime data).
  private _subheadingVars: any[] | null = null;
  private _descrVars: any[] | null = null;

  get subheadingVars(): any[] | null { return this._subheadingVars; }
  set subheadingVars(value: any[] | null) {
    this._subheadingVars = value;
    this._updateSubheading();
  }

  get descrVars(): any[] | null { return this._descrVars; }
  set descrVars(value: any[] | null) {
    this._descrVars = value;
    this._updateDescription();
  }

  static readonly observedAttributes = [
    'subheading-default-text', 'subheading-dict-key', 'descr-default-text', 'descr-text-dict-key',
  ];

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, old: string | null, next: string | null): void {
    if (old === next) return;
    if (name === 'subheading-default-text' || name === 'subheading-dict-key') this._updateSubheading();
    if (name === 'descr-default-text' || name === 'descr-text-dict-key') this._updateDescription();
  }

  connectedCallback(): void {
    this.style.display = 'contents';
    if (!this._built) this._build();
    // Reset on a new session (broadcast by createAndSetNewProject).
    window.addEventListener('as-inputs-reset', this._onResetBound);
  }

  disconnectedCallback(): void {
    this._observer?.disconnect();
    this._observer = null;
    window.removeEventListener('as-inputs-reset', this._onResetBound);
  }

  get previewActive(): boolean { return this._previewActive; }

  // ── Private ───────────────────────────────────────────────────────────────────

  /**
   * Restore the panel to its initial state on a new session: turn the preview
   * toggle off, clear the active flag, and notify listeners so any active preview
   * is torn down. No-ops if preview was already inactive.
   */
  private _handleSessionReset(): void {
    (this._toggleBtnEl as any)?.forceToggleState?.(false);
    if (!this._previewActive) return;
    this._previewActive = false;
    sendEv(this, 'as-preview-toggle', {
      detail: { active: false }
    });
  }

  private _build(): void {
    this._built = true;

    const group = document.createElement('as-param-group');
    this._cardEl = group;

    const p = document.createElement('p');
    p.className = 'desc';
    p.style.cssText = 'font-size:0.875rem;color:hsl(var(--muted-foreground));margin:0;';
    const textEl = document.createElement('as-text');
    this._applyDescription(textEl);
    this._descTextEl = textEl;
    p.appendChild(textEl);
    group.appendChild(p);

    const btn = document.createElement('as-button');
    btn.setAttribute('toggle', '');
    btn.setAttribute('name', 'as-export-preview-toggle');
    btn.setAttribute('default-text', 'Show export preview');
    btn.setAttribute('dict-key', 'fileIO.export.previewShow');
    btn.setAttribute('toggle-on-default-text', 'Hide export preview');
    btn.setAttribute('toggle-on-dict-key', 'fileIO.export.previewHide');
    this._toggleBtnEl = btn;
    btn.addEventListener('as-button-click', (e: Event) => {
      this._previewActive = (e as CustomEvent).detail?.toggledOn ?? false;
      sendEv(this, 'as-preview-toggle', {
        detail: { active: this._previewActive }
      });
    });
    group.appendChild(btn);

    // Adopt any children already in the element (from HTML authoring)
    for (const child of Array.from(this.children) as HTMLElement[]) {
      this._userEls.push(child);
      group.appendChild(child);
    }

    this.appendChild(group);
    this._updateSubheading();

    // Watch for children added dynamically after build
    this._observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          if (node instanceof HTMLElement && node !== group) {
            this._userEls.push(node);
            group.appendChild(node);
          }
        }
      }
    });
    this._observer.observe(this, { childList: true });
  }

  private _updateSubheading(): void {
    if (!this._cardEl) return;
    const authorText = this.getAttribute('subheading-default-text');
    const defaultText = authorText ?? 'Design preview';
    const dictKey = this.getAttribute('subheading-dict-key') ?? (authorText == null ? 'fileIO.export.previewHeading' : null);
    // vars set before dict-key so they're in place when the key subscription registers.
    if (this._subheadingVars) this._cardEl.setAttribute('subheading-vars', JSON.stringify(this._subheadingVars));
    else this._cardEl.removeAttribute('subheading-vars');
    if (dictKey) {
      this._cardEl.setAttribute('subheading-dict-key', dictKey);
      this._cardEl.setAttribute('subheading-default-text', defaultText);
    } else {
      this._cardEl.removeAttribute('subheading-dict-key');
      this._cardEl.setAttribute('subheading-default-text', defaultText);
    }
  }

  private _updateDescription(): void {
    if (!this._descTextEl) return;
    this._applyDescription(this._descTextEl);
  }

  private _applyDescription(el: HTMLElement): void {
    const authorText = this.getAttribute('descr-default-text');
    const defaultText = authorText ?? 'Preview your final, printable part before exporting.';
    const dictKey = this.getAttribute('descr-text-dict-key') ?? (authorText == null ? 'fileIO.export.previewDescription' : null);
    applyLabelVars(el, this._descrVars);
    applyLocalizedText(el, dictKey, defaultText);
  }

}

if (!customElements.get(AsExportPreviewPanel.tag)) {
  customElements.define(AsExportPreviewPanel.tag, AsExportPreviewPanel);
} else {
  console.warn('as-export-preview-panel already defined');
}
